import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Users as UsersIcon, Shield, ShieldOff, Loader2, ArrowLeft, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { API } from "@/config";
import ChangePasswordModal from "@/components/ChangePasswordModal";

export default function UsersPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);
  const [passwordModalUser, setPasswordModalUser] = useState(null);

  useEffect(() => {
    if (user?.role !== "admin") {
      navigate("/", { replace: true });
      return;
    }
    fetchUsers();
  }, [user?.role, navigate]);

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/users`);
      setUsers(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load users");
      if (err.response?.status === 403) navigate("/", { replace: true });
    } finally {
      setLoading(false);
    }
  };

  const setEnabled = async (userId, enabled) => {
    setUpdating(userId);
    try {
      await axios.patch(`${API}/users/${userId}`, { enabled });
      setUsers((prev) => prev.map((u) => (u.user_id === userId ? { ...u, enabled } : u)));
      toast.success(enabled ? "User enabled" : "User disabled");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    } finally {
      setUpdating(null);
    }
  };

  const setRole = async (userId, role) => {
    setUpdating(userId);
    try {
      await axios.patch(`${API}/users/${userId}`, { role });
      setUsers((prev) => prev.map((u) => (u.user_id === userId ? { ...u, role } : u)));
      toast.success(role === "admin" ? "User promoted to admin" : "User set to standard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    } finally {
      setUpdating(null);
    }
  };

  if (user?.role !== "admin") return null;
  if (loading) {
    return (
      <div className="p-8 flex justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-gray-500" />
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" size="sm" onClick={() => navigate("/")}>
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back
        </Button>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <UsersIcon className="w-7 h-7" />
          Users
        </h1>
      </div>
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">All users</CardTitle>
          <p className="text-sm text-gray-500">Enable/disable login and promote to admin.</p>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Email</TableHead>
                <TableHead>Display name</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Login</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((u) => (
                <TableRow key={u.user_id}>
                  <TableCell className="font-medium">{u.email}</TableCell>
                  <TableCell>{u.display_name || "—"}</TableCell>
                  <TableCell>
                    <Badge variant={u.role === "admin" ? "default" : "secondary"}>
                      {u.role === "admin" ? "Admin" : "User"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={u.enabled ? "default" : "destructive"} className={u.enabled ? "bg-green-600" : ""}>
                      {u.enabled ? "Enabled" : "Disabled"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      {u.user_id !== user?.user_id && (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={updating === u.user_id}
                          onClick={() => setEnabled(u.user_id, !u.enabled)}
                        >
                          {updating === u.user_id ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : u.enabled ? (
                            <>
                              <ShieldOff className="w-3 h-3 mr-1" />
                              Disable
                            </>
                          ) : (
                            <>
                              <Shield className="w-3 h-3 mr-1" />
                              Enable
                            </>
                          )}
                        </Button>
                      )}
                      {u.role !== "admin" && (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={updating === u.user_id}
                          onClick={() => setRole(u.user_id, "admin")}
                        >
                          Make admin
                        </Button>
                      )}
                      {u.role === "admin" && u.user_id !== user?.user_id && (
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={updating === u.user_id}
                          onClick={() => setRole(u.user_id, "user")}
                        >
                          Remove admin
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={updating === u.user_id}
                        onClick={() => setPasswordModalUser(u)}
                      >
                        <KeyRound className="w-3 h-3 mr-1" />
                        Change password
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <ChangePasswordModal
        open={!!passwordModalUser}
        onOpenChange={(open) => !open && setPasswordModalUser(null)}
        userId={passwordModalUser?.user_id}
        userLabel={passwordModalUser ? `${passwordModalUser.display_name || passwordModalUser.email}` : null}
      />
    </div>
  );
}
